import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faImage, faList, faSliders, faStar, } from "@fortawesome/free-solid-svg-icons";
import Routes from "../../routes";
import "./EndlineSetup.css";

import DefectSetupSection from './sections/DefectSetupSection';
import TopTenSetupSection from './sections/TopTenSetupSection';
import PartSetupSection from './sections/PartSetupSection';

const normalizeEnglishName = (value = "") => {
    const englishOnly = value.replace(/[^A-Za-z\s/&(),.'-]/g, "").replace(/^\s+/, "");
    return englishOnly
        ? `${englishOnly.charAt(0).toUpperCase()}${englishOnly.slice(1)}`
        : "";
};

const normalizeDefectCode = (value = "") =>
    value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
const showProcessingAlert = (title) =>
    Swal.fire({
        title,
        text: "Please wait...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
    });
export default function EndlineSetupPage() {
    const [params] = useSearchParams();
    const bu = (params.get("bu") || "G1").toUpperCase();
    const [section, setSection] = useState("defects");
    const [data, setData] = useState({
        categories: [],
        defects: [],
        top_ten: [],
        part_categories: [],
        parts: [],
    });
    const [categoryForm, setCategoryForm] = useState({
        code: "",
        th: "",
        en: "",
        vn: "",
        my: "",
    });
    const [defectForm, setDefectForm] = useState({
        category: "",
        code: "",
        th: "",
        en: "",
        vn: '',
        my: '',
        group_en: '',
        group_th: '',
        group_vn: '',
        group_my: '',
        image: null,
    });
    const [topCategory, setTopCategory] = useState("");
    const [topCodes, setTopCodes] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (silent = false) => {
        if (!silent) {
            setLoading(true);
        }
        try {
            const response = await axios.post(
                `${Routes.API}/api/endline2/setup-overview`,
                { bu },
            );
            const next = response.data || {};
            setData({
                categories: next.categories || [],
                defects: next.defects || [],
                top_ten: next.top_ten || [],
                part_categories: next.part_categories || [],
                parts: next.parts || [],
            });
            setDefectForm((prev) => ({
                ...prev,
                category: prev.category || next.categories?.[0]?.code || "",
            }));
            setTopCategory(
                (prev) =>
                    prev ||
                    next.part_categories?.[0] ||
                    next.top_ten?.[0]?.product_category ||
                    "",
            );
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Load setup failed",
                text: error.response?.data?.message || error.message,
            });
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [bu]);

    useEffect(() => {
        load();
    }, [load]);

        const sewingDefectCodes = useMemo(() => new Set(
        data.defects
            .filter((item) => [item.product_category, item.product_category_eng, item.product_category_thai]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes("sewing defect"))
            .map((item) => item.defect_code),
    ), [data.defects]);
    const selectedTop = useMemo(
        () => (data.top_ten.find((item) => item.product_category === topCategory)
            ?.defect_codes || []).filter((code) => sewingDefectCodes.has(code)),
        [data.top_ten, sewingDefectCodes, topCategory],
    );useEffect(() => {
        setTopCodes(selectedTop);
    }, [selectedTop]);
    const topProductCategories = useMemo(
        () => [
            ...new Set([
                ...data.part_categories,
                ...data.top_ten
                    .map((item) => item.product_category)
                    .filter(Boolean),
            ]),
        ],
        [data.part_categories, data.top_ten],
    );
    const categoryDefects = useMemo(() => data.defects, [data.defects]);

    const saveCategory = async (event) => {
        event.preventDefault();
        const categoryPayload = {
            ...categoryForm,
            en: normalizeEnglishName(categoryForm.en),
        };
        showProcessingAlert("Saving category...");
        try {
            await axios.post(`${Routes.API}/api/endline2/setup/category`, categoryPayload);
            setCategoryForm({ code: "", th: "", en: "", vn: "", my: "" });
            await load();
        } finally {
            Swal.close();
        }
    };
    const deleteCategory = async (code) => {
        showProcessingAlert("Deleting category...");
        try {
            await axios.post(`${Routes.API}/api/endline2/setup/delete-category`, { code });
            setCategoryForm({ code: "", th: "", en: "", vn: "", my: "" });
            await load(true);
        } finally {
            Swal.close();
        }
    };

    const deleteDefect = async (code) => {
        showProcessingAlert("Deleting defect...");
        try {
            await axios.post(`${Routes.API}/api/endline2/setup/delete-defect`, { code });
            await load(true);
        } finally {
            Swal.close();
        }
    };
    const saveDefect = async (event) => {
        event.preventDefault();
        const form = new FormData();
        Object.entries({
            ...defectForm,
            code: normalizeDefectCode(defectForm.code),
            en: normalizeEnglishName(defectForm.en),
            group_en: normalizeEnglishName(defectForm.group_en),
        }).forEach(([key, value]) => value !== null && form.append(key, value));
        showProcessingAlert("Saving defect...");
        try {
            await axios.post(`${Routes.API}/api/endline2/setup/defect`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDefectForm({
                category: data.categories[0]?.code || "",
                code: "",
                th: "",
                en: "",
                vn: "",
                my: "",
                group_en: "",
                group_th: "",
                group_vn: "",
                group_my: "",
                image: null,
            });
            await load();
        } finally {
            Swal.close();
        }
    };
    const saveTopTen = async () => {
        showProcessingAlert("Saving Top 10...");
        try {
            await axios.post(`${Routes.API}/api/endline2/setup/top-ten`, {
                product_category: topCategory,
                defect_codes: topCodes,
            });
            await load();
        } finally {
            Swal.close();
        }
    };
    const toggleTop = (code) =>
        setTopCodes((prev) =>
            prev.includes(code)
                ? prev.filter((item) => item !== code)
                : prev.length < 10
                  ? [...prev, code]
                  : prev,
        );

    const setupNav = [
        ["defects", "Defect master", faList],
        ["top-ten", "Top 10", faStar],
        ["parts", "Part & image", faImage],
    ];

    return (
        <main className="endline-setup-page">
            <header className="setup-header">
                <div className="setup-brand">
                    <FontAwesomeIcon icon={faSliders} />
                    <div>
                        <span>Endline QC</span>
                        <h1>Setup</h1>
                    </div>
                </div>
                <Link className="setup-back-link" to={`/Endline2/${bu}/001`}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </Link>
            </header>
            <div className="setup-layout">
                <aside className="setup-sidebar" aria-label="Setup navigation">
                    {setupNav.map(([id, label, icon]) => (
                        <button
                            key={id}
                            aria-label={label}
                            data-tooltip={label}
                            className={section === id ? "active" : ""}
                            onClick={() => setSection(id)}
                        >
                            <FontAwesomeIcon icon={icon} />
                        </button>
                    ))}
                </aside>
                <section className="setup-content">
                    {loading ? (
                        <div className="setup-loading">
                            Loading setup data...
                        </div>
                    ) : (
                        <>
                            {section === "defects" && <DefectSetupSection data={data} categoryForm={categoryForm} setCategoryForm={setCategoryForm} defectForm={defectForm} setDefectForm={setDefectForm} onSaveCategory={saveCategory} onSaveDefect={saveDefect} onDeleteCategory={deleteCategory} onDeleteDefect={deleteDefect} onReload={() => load(true)} apiBase={Routes.API} />}
                            {section === "top-ten" && <TopTenSetupSection topCategory={topCategory} setTopCategory={setTopCategory} topProductCategories={topProductCategories} categoryDefects={categoryDefects} topCodes={topCodes} onToggle={toggleTop} onSave={saveTopTen} />}
                            {section === "parts" && <PartSetupSection data={data} onReload={() => load(true)} apiBase={Routes.API} />}
                        </>
                    )}
                </section>
            </div>
        </main>
    );
}
